/**
 * Audit trail utilities for Terms of Service and Privacy Policy acceptance.
 *
 * Both ToS and Privacy Policy use the same AWSJSON structure:
 *   { "version-key": { "accepted": "ISO-8601 date" }, ... }
 *
 * Version keys are now the `issuedDate` from the document frontmatter
 * (e.g. "2026-05-01"). Old entries keyed by S3 paths are preserved for
 * historical record and backward-compatible acceptance checking.
 *
 * Each acceptance appends a new entry. Old entries are never deleted,
 * creating a full audit trail.
 */

import { getTermsOfService, getPrivacyPolicy } from "./documentLoader";

export interface AcceptanceRecord {
  accepted: string; // ISO 8601
}

export type AuditTrail = Record<string, AcceptanceRecord>;

/** issuedDate of the current Terms of Service document. */
export function getCurrentTermsIssuedDate(): string {
  return getTermsOfService().issuedDate;
}

/** issuedDate of the current Privacy Policy document. */
export function getCurrentPrivacyPolicyIssuedDate(): string {
  return getPrivacyPolicy().issuedDate;
}

/** Parse an AWSJSON string into a typed AuditTrail. Handles null, undefined, and malformed data. */
export function parseAuditTrail(raw: string | null | undefined): AuditTrail {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as AuditTrail;
    }
    return {};
  } catch {
    return {};
  }
}

/** Check whether the current document version has been accepted by looking for its issuedDate key. */
export function hasAcceptedCurrentVersion(trail: AuditTrail, issuedDate: string): boolean {
  return issuedDate in trail;
}

/** Check whether the trail has any older acceptances (for showing "updated" banners). */
export function hasOlderAcceptances(trail: AuditTrail, currentKey: string): boolean {
  const keys = Object.keys(trail);
  if (keys.length === 0) return false;
  // Has entries but hasn't accepted the current version via direct key
  return !keys.includes(currentKey);
}

/** Append a new acceptance entry to the trail keyed by issuedDate. Returns AWSJSON string. */
export function addAcceptance(existingTrail: AuditTrail, issuedDate: string): string {
  const updated: AuditTrail = {
    ...existingTrail,
    [issuedDate]: {
      accepted: new Date().toISOString(),
    },
  };
  return JSON.stringify(updated);
}

// ---------------------------------------------------------------------------
// Organization AI Analyst (chat) access
// ---------------------------------------------------------------------------

/**
 * Minimal organization shape needed for chat access checks.
 * `orgAiAnalystAccess` reads as undefined until the deployed schema field is
 * fetched, which safely resolves to "no access".
 */
export interface OrgChatAccessOrganization {
  id: string;
  name?: string | null;
  orgAiAnalystAccess?: boolean | null;
}

export type OrgChatAccessStatus =
  | "loading" // organizations not loaded yet
  | "allowed" // admin, no organization, or an organization with access enabled
  | "blocked"; // org user whose organization(s) don't have access enabled

export interface OrgChatAccessCheck {
  status: OrgChatAccessStatus;
  /** The viewer's organization (first one) — used to tag access requests. */
  organizationId?: string;
  organizationName?: string;
}

/**
 * Decide whether the viewer can use the AI Analyst chat.
 * Admins always can; users with no organization can; org users (owner or
 * member) can only when an admin has enabled access for one of their
 * organizations. Blocked users can request access.
 */
export function getOrgChatAccessStatus(args: {
  isAdmin: boolean;
  organizationsLoaded: boolean;
  organizations: Record<string, OrgChatAccessOrganization> | null | undefined;
}): OrgChatAccessCheck {
  const { isAdmin, organizationsLoaded, organizations } = args;

  if (isAdmin) return { status: "allowed" };
  if (!organizationsLoaded) return { status: "loading" };

  const orgs = Object.values(organizations || {});
  if (orgs.length === 0) return { status: "allowed" };

  const enabledOrg = orgs.find((org) => org?.orgAiAnalystAccess === true);
  if (enabledOrg) return { status: "allowed" };

  // Blocked. A user can belong to multiple blocked orgs
  const blockedOrg = [...orgs]
    .filter((org) => org?.id)
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))[0];

  return {
    status: "blocked",
    organizationId: blockedOrg?.id,
    organizationName: blockedOrg?.name ?? undefined,
  };
}

export interface ConsentModalCheck {
  show: boolean;
  termsNeeded: boolean;
  privacyNeeded: boolean;
}

/**
 * Determine whether the platform consent modal should be shown.
 * Checks both Terms of Service and Privacy Policy audit trails.
 */
export function shouldShowConsentModal(
  termsTrailRaw: string | null | undefined,
  privacyTrailRaw: string | null | undefined
): ConsentModalCheck {
  const termsIssuedDate = getCurrentTermsIssuedDate();
  const privacyIssuedDate = getCurrentPrivacyPolicyIssuedDate();

  const termsTrail = parseAuditTrail(termsTrailRaw);
  const privacyTrail = parseAuditTrail(privacyTrailRaw);

  const termsNeeded = !hasAcceptedCurrentVersion(termsTrail, termsIssuedDate);
  const privacyNeeded = !hasAcceptedCurrentVersion(privacyTrail, privacyIssuedDate);

  return {
    show: termsNeeded || privacyNeeded,
    termsNeeded,
    privacyNeeded,
  };
}
